import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useGlobalSearch from "hooks/useGlobalSearch";

export default function useMobileSearchModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);
  const [selectedCategoryForView, setSelectedCategoryForView] =
    useState(null);

  const {
    searchQuery,
    setSearchQuery,
    activeCategories,
    setActiveCategory,
    results,
    loading,
    error,
  } = useGlobalSearch();

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onClose?.();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSelectedCategoryForView(null);
  };

  const handleViewAll = (category) => {
    setSelectedCategoryForView(category);
  };

  const getCurrentResults = () => {
    if (selectedCategoryForView) {
      return results[selectedCategoryForView] || [];
    }
    if (activeCategories.length === 1) {
      return results[activeCategories[0]] || [];
    }
    return [];
  };

  const getCurrentCategory = () => {
    if (selectedCategoryForView) {
      return selectedCategoryForView;
    }
    if (activeCategories.length === 1) {
      return activeCategories[0];
    }
    return null;
  };

  const showGroupedResults =
    (activeCategories.length === 0 || activeCategories.length >= 2) &&
    !selectedCategoryForView;

  return {
    t,
    searchInputRef,
    searchQuery,
    setSearchQuery,
    activeCategories,
    results,
    loading,
    error,
    handleCategoryChange,
    handleViewAll,
    getCurrentResults,
    getCurrentCategory,
    showGroupedResults,
  };
}
